import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { FilterProvider } from './context/FilterContext';
import config from './config';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import ApiAnalytics from './pages/ApiAnalytics';
import RealTimeMonitor from './pages/RealTimeMonitor';
import ConversationInspector from './pages/ConversationInspector';
import CostCenter from './pages/CostCenter';
import UserActivity from './pages/UserActivity';
import Settings from './pages/Settings';
import ModelTesting from './pages/ModelTesting';
import TrafficControls from './pages/TrafficControls';
import Onboarding from './pages/Onboarding';

function FullScreenSpinner({ message }) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-white">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-sce-green border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-500">{message}</p>
      </div>
    </div>
  );
}

function App() {
  const { isAuthenticated, isLoading, isRedirecting, authConfigured, login, isAdmin, hasRole } = useAuth();

  // Auto-login is on whenever auth is configured and not explicitly disabled.
  const autoLoginActive = authConfigured && config.auth.autoLogin;

  // Self-healing fallback: if we're settled (not loading) and still not
  // authenticated while auto-login is active, ensure a redirect is kicked off
  // even if the context-level effect didn't run for any reason.
  useEffect(() => {
    if (autoLoginActive && !isLoading && !isAuthenticated && !isRedirecting) {
      login();
    }
  }, [autoLoginActive, isLoading, isAuthenticated, isRedirecting, login]);

  if (isLoading) {
    return <FullScreenSpinner message="Loading…" />;
  }

  // Not signed in → never render the app shell. Either bounce to AAD
  // (auto-login) or show the manual sign-in page. Checking isAuthenticated
  // BEFORE anything else guarantees no protected content, and no cached
  // identity, can flash on screen.
  if (!isAuthenticated) {
    if (isRedirecting || autoLoginActive) {
      return <FullScreenSpinner message="Redirecting to sign-in…" />;
    }
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-center bg-white rounded-2xl p-10 shadow-lg border border-gray-200">
          <h1 className="text-2xl font-bold mb-2 text-sce-dark">LLM Gateway Observability</h1>
          <p className="text-gray-500 mb-6">Sign in to access the dashboard</p>
          <button
            onClick={login}
            className="px-6 py-3 bg-sce-green text-white rounded-lg hover:opacity-90 transition font-medium"
          >
            Sign in with Azure AD
          </button>
        </div>
      </div>
    );
  }

  return (
    <FilterProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/apis" element={<ApiAnalytics />} />
          <Route path="/realtime" element={<RealTimeMonitor />} />
          <Route path="/conversations" element={<ConversationInspector />} />
          <Route path="/costs" element={<CostCenter />} />
          <Route path="/users" element={<UserActivity />} />
          {/* APIM Consumer Onboarding — any authenticated user can submit;
              the approval queue inside is gated by the EASP role. */}
          <Route path="/onboarding" element={<Onboarding />} />
          {/* Model Testing is gated by its own role (EASP_Admin by default);
              Settings remains admin-only. Non-entitled users hitting the URL
              directly are bounced to the dashboard. */}
          <Route
            path="/model-testing"
            element={hasRole(config.rbac.modelTestRole) ? <ModelTesting /> : <Navigate to="/" replace />}
          />
          <Route
            path="/settings"
            element={isAdmin ? <Settings /> : <Navigate to="/" replace />}
          />
          {/* Model Kill Switch — admin-only, same gate as Settings. The API
              enforces the role server-side; this just avoids rendering it. */}
          <Route
            path="/traffic-controls"
            element={isAdmin ? <TrafficControls /> : <Navigate to="/" replace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </FilterProvider>
  );
}

export default App;
